import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PROJECTS, STATUS_NOTE, getCategory, projectPath } from "@/data/site";
import { PageHero } from "@/components/PageHero";
import { FadeUp, Eyebrow } from "@/components/Reveal";
import { Photo } from "@/components/Photo";
import { StatusBadge } from "@/components/StatusBadge";
import { Seo } from "@/components/Seo";
import { ProjectDetails } from "@/components/ProjectDetails";
import NotFound from "@/pages/NotFound";

const splitName = (name) => {
  const w = name.split(" ");
  const mid = Math.ceil(w.length / 2);
  return w.length > 2 ? [w.slice(0, mid).join(" "), w.slice(mid).join(" ")] : [name];
};

export default function Project() {
  const { category, project } = useParams();
  const idx = PROJECTS.findIndex((p) => p.slug === project && p.category === category);
  if (idx < 0) return <NotFound />;
  const p = PROJECTS[idx];
  const cat = getCategory(p.category);
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  return (
    <>
      <Seo title={p.name} path={projectPath(p)} description={`${p.name} (${p.status}) at Little Englishman's Bay Estate, Tobago. ${p.summary}`} />
      <PageHero eyebrow={cat.title} lines={splitName(p.name)} image={p.heroImage ?? p.image} alt={p.name} testid="project-hero">
        <span className="lg:hidden"><StatusBadge status={p.status} dark testid="project-hero-status" /></span>
        <span className="hidden lg:inline"><StatusBadge status={p.status} /></span>
      </PageHero>

      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28" data-testid="project-body">
        <div className="grid gap-14 lg:grid-cols-12">
          <FadeUp className="lg:col-span-4">
            <Link to={`/${cat.slug}`} className="inline-flex items-center gap-2 font-caps text-[12px] tracking-[0.2em] uppercase text-heritage hover:text-gold-deep" data-testid="project-back-link">
              <ArrowLeft size={14} /> {cat.title}
            </Link>
            <dl className="mt-10 border-t border-gold/40">
              {[
                ["Area", cat.title],
                ["Status", <StatusBadge key="s" status={p.status} testid="project-status-badge" />],
                ["Location", "Little Englishman's Bay Estate, Tobago"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-4 border-b border-gold/25 py-5">
                  <dt className="font-caps text-[11px] tracking-[0.24em] uppercase text-gold-deep">{k}</dt>
                  <dd className="text-right text-heritage">{v}</dd>
                </div>
              ))}
            </dl>
          </FadeUp>
          <div className="lg:col-span-7 lg:col-start-6">
            <FadeUp>
              <Eyebrow>About the project</Eyebrow>
              <p className="mt-6 font-display text-3xl leading-[1.25] text-heritage md:text-[2.5rem]" data-testid="project-description">{p.summary}</p>
              <p className="mt-8 border-l-2 border-gold pl-5 text-lg italic text-muted-ink" data-testid="project-status-note">{STATUS_NOTE[p.status]}</p>
            </FadeUp>
          </div>
        </div>
      </section>

      {p.details && <ProjectDetails details={p.details} />}

      {p.plan && (
        <section className={`mx-auto max-w-[1440px] px-5 pb-20 md:px-10 md:pb-28 ${p.details ? "pt-20 md:pt-28" : ""}`} data-testid="project-plan">
          <FadeUp>
            <Eyebrow>{p.plan.title}</Eyebrow>
            <figure className="mt-6 border border-gold/50 p-2 md:p-3">
              <img src={p.plan.src} alt={p.plan.alt} loading="lazy" className="block h-auto w-full" />
            </figure>
            {p.plan.caption && <p className="mt-3 text-sm italic text-muted-ink">{p.plan.caption}</p>}
          </FadeUp>
        </section>
      )}

      {p.gallery && (
        <section className="mx-auto max-w-[1440px] px-5 pb-20 md:px-10 md:pb-28" data-testid="project-gallery">
          <FadeUp><Eyebrow>Gallery</Eyebrow></FadeUp>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {p.gallery.map((g, i) => (
              <FadeUp key={g.src} delay={i * 0.08}>
                <figure className="border border-gold/50 p-2 md:p-3" data-testid={`project-gallery-${i}`}>
                  <img src={g.src} alt={g.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                  <figcaption className="px-1 pt-3 font-caps text-[12px] tracking-[0.2em] uppercase text-gold-deep">{g.caption}</figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      <Link to={projectPath(next)} className="group relative block h-[52vh] min-h-[380px] overflow-hidden" data-testid="next-project-link">
        <Photo image={next.image} alt={next.name} className="absolute inset-0" imgClassName="transition-transform duration-[1.8s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.06]" tagClassName="right-4 top-4" />
        <div className="absolute inset-0 bg-heritage-deep/60 transition-colors duration-700 group-hover:bg-heritage-deep/45" />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-5 text-center">
          <p className="font-caps text-[12px] tracking-[0.34em] uppercase text-gold-light">Next project</p>
          <h2 className="mt-4 font-display text-4xl text-ivory md:text-7xl">{next.name}</h2>
          <span className="mt-5 h-px w-16 bg-gold transition-[width] duration-700 group-hover:w-40" />
        </div>
      </Link>
    </>
  );
}
